import React from 'react';

/** Shared with order wizard cards, timeline widgets, and collapsible sections. */
export const SECTION_CARD_HEADER =
  'flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-primary-50/40 px-4 py-3.5 sm:px-5';
export const SECTION_CARD_HEADER_MAIN = 'flex min-w-0 flex-1 items-center gap-3';
export const SECTION_CARD_ICON =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-primary-200/80 bg-white text-primary-700 shadow-sm ring-1 ring-primary-100';
export const SECTION_CARD_TITLE_COL = 'min-w-0 border-l-2 border-primary-500 pl-3';
export const SECTION_CARD_TITLE = 'truncate text-sm font-extrabold tracking-tight text-slate-900';
export const SECTION_CARD_DESCRIPTION = 'mt-0.5 truncate text-xs text-slate-500';
/** Right side of section headers (matches Design “Add beam”, DetailSection actions). */
export const SECTION_CARD_ACTIONS = 'flex shrink-0 flex-wrap items-center justify-end gap-2';

/**
 * DetailSection — a consistent grouped card for read-only "key : value" detail views.
 * Used inside preview / detail modals so every screen shares the same visual rhythm.
 */
export function DetailSection({
  title,
  description,
  icon,
  actions,
  children,
  columns = 2,
  className = '',
  bodyClassName = '',
}) {
  const colsClass =
    columns === 1
      ? 'grid-cols-1'
      : columns === 3
        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
        : columns === 4
          ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
          : 'grid-cols-1 sm:grid-cols-2';

  return (
    <section className={`rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden ${className}`}>
      {(title || actions) && (
        <header className={SECTION_CARD_HEADER}>
          <div className={SECTION_CARD_HEADER_MAIN}>
            {icon && <span className={SECTION_CARD_ICON}>{icon}</span>}
            <div className={SECTION_CARD_TITLE_COL}>
              {title && <h3 className={SECTION_CARD_TITLE}>{title}</h3>}
              {description && <p className={SECTION_CARD_DESCRIPTION}>{description}</p>}
            </div>
          </div>
          {actions && <div className={SECTION_CARD_ACTIONS}>{actions}</div>}
        </header>
      )}
      <div className={`p-4 sm:p-5 ${bodyClassName}`}>
        {React.Children.count(children) > 0 && (
          <dl className={`grid gap-x-6 gap-y-3 ${colsClass}`}>{children}</dl>
        )}
      </div>
    </section>
  );
}

/**
 * DetailField — a single "Label / Value" pair laid out so columns align cleanly.
 * Use `span` to make a value span multiple columns (e.g. long address).
 */
export function DetailField({
  label,
  value,
  children,
  span,
  mono = false,
  className = '',
}) {
  const spanClass =
    span === 'full'
      ? 'sm:col-span-full'
      : span === 2
        ? 'sm:col-span-2'
        : span === 3
          ? 'sm:col-span-3'
          : '';
  const display = children ?? (value == null || value === '' ? '—' : value);
  return (
    <div className={`min-w-0 ${spanClass} ${className}`}>
      <dt className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
        {label}
      </dt>
      <dd
        className={`mt-0.5 text-sm text-slate-900 break-words ${
          mono ? 'font-mono tabular-nums' : 'font-medium'
        }`}
      >
        {display}
      </dd>
    </div>
  );
}

export default DetailSection;
